import {
    RiArrowDownSLine, RiArrowUpSLine, RiBookOpenLine,
    RiShieldCheckLine,
} from "@remixicon/react";
import classNames from "classnames";
import React, { useState } from "react";
import { WorkflowService } from "../../../../__generated__/api";
import Avatar from "../../../../components/avatar/Avatar";
import ExpandableText from "../../../../components/expanding-section/ExpandableText";
import { HtmlRenderer } from "../../../../components/html-render/HtmlRenderer";
import Badge from "../../../../components/theme/badge/Badge";
import Button from "../../../../components/theme/button/Button";
import Link from "../../../../components/theme/button/Link";
import Icon from "../../../../components/theme/icon/Icon";
import Text from "../../../../components/theme/text/Text";
import Styles from "./Services.module.scss";

function ButtonLink({
    url,
    text,
    className,
}: {
    url: string | undefined | null,
    text: string;
    className?: string;
}) {
    return url ? (
        <Link
            href={url}
            target="_blank"
            size="small"
            variant="outline-gray"
            prefixIcon={<Icon.Link />}
            className={className}
        >
            {text}
        </Link>
    ) : undefined;
}
ButtonLink.defaultProps = {
    className: undefined,
};

const MAX_ACTIONS_DISPLAY = 3;
export default function ViewServiceCard({
    service,
    className,
    isLoading,
    isFullPage,
}: {
    service: WorkflowService;
    className?: string;
    isLoading: boolean;
    isFullPage?: boolean;
}) {
    const [isExpandedActions, setExpandedActions] = useState(isFullPage);
    let { actions } = service;
    if (!isExpandedActions) {
        actions = actions?.limit(MAX_ACTIONS_DISPLAY);
    }

    const hasPrivacyUrl = service.learnMore?.privacyPolicyUrl
        || service.learnMore?.dataProtectionUrl
        || service.learnMore?.securityCenterUrl;
    const hasLink = service.learnMore?.websiteUrl
        || service.learnMore?.supportCenterUrl
        || service.learnMore?.termsOfServiceUrl;

    const loadingClass = isLoading ? Styles.loading : "";

    const links = (
        <>
            {hasLink && (
                <div className={Styles["learn-more"]}>
                    <Text as="h3" textVariant="body-1-semibold" colorVariant="text-title">
                        <Icon icon={<RiBookOpenLine />} variant="default" size="small" className={loadingClass} />
                        {" "}
                        <span className={loadingClass}>
                            Learn More
                        </span>
                    </Text>
                    <div className={Styles["learn-more-row"]}>
                        <ButtonLink
                            className={loadingClass}
                            url={service.learnMore?.websiteUrl}
                            text="Documentation"
                        />
                        <ButtonLink
                            className={loadingClass}
                            url={service.learnMore?.supportCenterUrl}
                            text="Get Support"
                        />
                        <ButtonLink
                            className={loadingClass}
                            url={service.learnMore?.termsOfServiceUrl}
                            text="Terms of Service"
                        />
                    </div>
                </div>
            )}
            {(isLoading || hasPrivacyUrl) && (
                <div className={Styles["learn-more"]}>
                    <Text
                        as="h3"
                        textVariant="body-1-semibold"
                        colorVariant="text-title"
                    >
                        <Icon icon={<RiShieldCheckLine />} variant="default" size="small" className={loadingClass} />
                        {" "}
                        <span className={loadingClass}>
                            Privacy and Security
                        </span>
                    </Text>
                    <div className={Styles["learn-more-row"]}>
                        <ButtonLink
                            className={loadingClass}
                            url={service.learnMore?.privacyPolicyUrl}
                            text="Privacy Policy"
                        />
                        <ButtonLink
                            className={loadingClass}
                            url={service.learnMore?.dataProtectionUrl}
                            text="Data Protection"
                        />
                        <ButtonLink
                            className={loadingClass}
                            url={service.learnMore?.securityCenterUrl}
                            text="Security Center"
                        />
                    </div>
                </div>
            )}
        </>
    );
    return (
        <div className={classNames(Styles.service, className, isLoading ? "loading" : "", isFullPage ? Styles["full-page"] : "")}>
            <div className={Styles["service-about"]}>
                <div className={Styles["service-about-description"]}>
                    <div className={Styles["service-header"]}>
                        <Avatar
                            isCircle={false}
                            variant="sm-3"
                            src={service?.squareIconUrl as string}
                            className={Styles["workflow-card-icon"]}
                            shouldShowLoading={false}
                        />

                        <Text as="strong" textVariant="subtitle-1">
                            {service?.name}
                        </Text>

                    </div>

                    <ExpandableText>
                        <Text as="div" className={loadingClass} textVariant="body-2-regular" colorVariant="body-secondary">
                            {isLoading ? (
                                <>
                                    This is a sample description that is a bit longer and it has a bunch
                                    of stuff. This is a sample description that is a bit longer and it has
                                    a bunch of stuff.
                                </>
                            ) : (
                                <HtmlRenderer html={service?.longDescription as string} shouldTrimBottom />
                            )}
                        </Text>
                    </ExpandableText>
                </div>

                {isFullPage && (
                    <div className={Styles["service-about-links"]}>
                        {links}
                    </div>
                )}
            </div>

            {service.actions?.length && (
                <div className={Styles["service-actions"]}>
                    <Text as="h3" textVariant="body-1-semibold" colorVariant="text-title">
                        Avalable Actions
                        {" "}
                        <Badge variant="counter">{service.actions?.length}</Badge>
                    </Text>

                    <div className={Styles["service-action-list"]}>
                        {actions?.map(action => (
                            <div key={action.id} className={Styles["service-action"]}>
                                <Icon.AutomationAction size="auto" variant="primary" background="primary" isRounded />
                                <Text textVariant="body-1-semibold" colorVariant="text-title">
                                    {action.name}
                                </Text>
                                <Text textVariant="body-2-regular" colorVariant="body-secondary">
                                    {action.description}
                                </Text>
                            </div>
                        ))}
                    </div>

                    {!isFullPage && service.actions?.length > MAX_ACTIONS_DISPLAY && (
                        <Button
                            onClick={() => setExpandedActions(current => !current)}
                            variant="none"
                            className={Styles["expandable-text-button"]}
                            postfixIcon={isExpandedActions
                                ? <RiArrowUpSLine />
                                : <RiArrowDownSLine />}
                        >
                            {isExpandedActions ? "Show Less" : "Show All"}
                        </Button>
                    )}
                </div>
            )}
            {!isFullPage && links}

        </div>
    );
}

ViewServiceCard.defaultProps = {
    className: "",
    isFullPage: false,
};
